// =============================================================================
// PEDRA — src/components/PartsTable.tsx
// Tabela de PECAS (paineis a produzir). Monta as colunas da EditableGrid a
// partir das configuracoes do projeto e cuida da barra de acoes da secao.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { EdgeBandType, Id, Part, Settings, Unit } from '../core/types';
import { isImperial, parseDimension } from '../core/units';
import { isValidHexColor, validatePart } from '../core/validation';
import { EditableGrid, formatDimensionExact, parseIntegerText } from './EditableGrid';
import type { ColumnDef } from './EditableGrid';

// -----------------------------------------------------------------------------
// UNIDADE VISIVEL
//
// O app aceita a medida exatamente como ela foi digitada — e por isso mesmo
// precisa dizer, na propria tabela, em que unidade ele esta lendo. Sem isso,
// "3,07" num projeto salvo em milimetro vira uma chapa de 3 mm sem que nada na
// tela contradiga o que o usuario acha que digitou.
// -----------------------------------------------------------------------------

/** Sigla curta da unidade, como aparece no cabecalho: `(m)`, `(mm)`, `(pol)`. */
const UNIT_ABBR: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'mm',
  cm: 'cm',
  m: 'm',
  in: 'pol',
  ft: 'pé',
});

/** Nome por extenso, para os tooltips e o selo da secao. */
const UNIT_NAME: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'milímetros',
  cm: 'centímetros',
  m: 'metros',
  in: 'polegadas',
  ft: 'pés',
});

/** A MESMA medida (3,07 m) escrita em cada unidade: o exemplo do erro real. */
const UNIT_EXAMPLE: Readonly<Record<Unit, string>> = Object.freeze({
  mm: '3070',
  cm: '307',
  m: '3,07',
  in: '120 7/8"',
  ft: `10' 0 7/8"`,
});

/** Par comprimento/largura de uma peca tipica, na unidade ativa (60 × 40 cm). */
const PASTE_EXAMPLE: Readonly<Record<Unit, string>> = Object.freeze({
  mm: '600\t400',
  cm: '60\t40',
  m: '0,6\t0,4',
  in: '24\t16',
  ft: '2\t1,4',
});

/**
 * Cabecalho de coluna de medida em DUAS linhas: rotulo em cima, unidade
 * embaixo. `ColumnDef.header` e' texto puro, entao a quebra vai no proprio
 * texto — `grid.css` a interpreta com `white-space: pre-line`. Duas linhas em
 * vez de "COMPRIMENTO (m)" numa so porque a coluna tem 74px: numa linha so, a
 * unidade — justamente o que precisa ser lido — seria a primeira a ser cortada.
 */
function dimensionHeader(label: string, unit: Unit): string {
  return `${label}\n(${UNIT_ABBR[unit]})`;
}

/**
 * Selo da unidade ativa ao lado do titulo da secao. Com `onOpenSettings` ele e'
 * um botao que abre o seletor de unidade em um clique; sem o callback, vira
 * legenda com `title` — visivel do mesmo jeito, que e' o essencial.
 */
function UnitChip(props: { unit: Unit; onOpenSettings?: (() => void) | undefined }): ReactElement {
  const { unit, onOpenSettings } = props;
  const text = `Medidas em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]})`;
  const base = `Tudo que você digitar nesta tabela é lido em ${UNIT_NAME[unit]} — ex.: ${UNIT_EXAMPLE[unit]}.`;

  if (onOpenSettings) {
    return (
      <button
        type="button"
        className="grid-panel__unit"
        onClick={onOpenSettings}
        title={`${base} Clique para trocar a unidade.`}
      >
        <span aria-hidden="true">📐</span>
        {text}
      </button>
    );
  }
  return (
    <span
      className="grid-panel__unit grid-panel__unit--static"
      title={`${base} Para trocar, abra Opções → Configurações → Unidade.`}
    >
      <span aria-hidden="true">📐</span>
      {text}
    </span>
  );
}

export interface PartsTableProps {
  parts: readonly Part[];
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
  /** Materiais ja usados no projeto, para o datalist. */
  materials: readonly string[];
  onChange(id: Id, patch: Partial<Part>): void;
  onAdd(): void;
  onDelete(ids: Id[]): void;
  onDuplicate(ids: Id[]): void;
  onPaste(text: string): void;
  onReorder(ids: Id[], toIndex: number): void;
  onClear(): void;
  onOpenEdgeBanding(partId: Id): void;
  /**
   * Abre as configuracoes no seletor de unidade. Opcional: quando o pai nao
   * passa nada, o selo da unidade continua visivel, so nao clicavel.
   */
  onOpenSettings?(): void;
  highlightedPartId?: Id | null;
  onHoverPart?(id: Id | null): void;
}

/** Linha "em branco": serve para a linha vazia automatica do fim da grade. */
function isBlankPart(p: Part): boolean {
  return p.length === 0 && p.width === 0 && p.label.trim() === '';
}

export function PartsTable(props: PartsTableProps): ReactElement {
  const {
    parts,
    settings,
    edgeBands,
    materials,
    onChange,
    onAdd,
    onDelete,
    onDuplicate,
    onPaste,
    onReorder,
    onClear,
    onOpenEdgeBanding,
    onOpenSettings,
    highlightedPartId,
    onHoverPart,
  } = props;

  const [selected, setSelected] = useState<Id[]>([]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const unit = settings.unit;

  // ---------------------------------------------------------------------------
  // COLUNAS
  // ---------------------------------------------------------------------------
  const columns = useMemo<Array<ColumnDef<Part>>>(() => {
    // A grade mostra a medida EXATA que o usuario digitou (so apara zeros a
    // direita): `settings.decimalPlaces` governa o diagrama, as estatisticas e
    // os relatorios, nunca a tabela — arredondar aqui destruiria o dado ao
    // reabrir a celula (3,07 virava 3,1).
    const showDim = (v: number): string => (v > 0 ? formatDimensionExact(v, unit) : '');
    const dimHint = `Medida inválida. Digite o valor em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) com vírgula decimal (ex.: ${UNIT_EXAMPLE[unit]}) ou como fração de polegada (ex.: 16 1/2").`;
    // Teclado do celular: numerico com vírgula no metrico. Em polegada/pé fica
    // o teclado completo — `16 1/2"` precisa de espaço, barra e aspas, que o
    // teclado decimal do iPhone simplesmente não tem.
    const dimInputMode = isImperial(unit) ? 'text' : 'decimal';

    const lengthCol: ColumnDef<Part> = {
      key: 'length',
      header: dimensionHeader('COMPRIMENTO', unit),
      width: 'minmax(74px, 0.7fr)',
      align: 'end',
      editor: 'dimension',
      inputMode: dimInputMode,
      title: `Comprimento da peça, em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) — ex.: ${UNIT_EXAMPLE[unit]}. Aceita vírgula e frações (16 1/2").`,
      errorHint: dimHint,
      format: (p) => showDim(p.length),
      toInput: (p) => showDim(p.length),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '') return { length: 0 };
        const v = parseDimension(text, unit);
        if (v === null || v < 0) return null;
        return { length: v };
      },
      validate: (p) =>
        !p.enabled || isBlankPart(p) || p.length > 0
          ? null
          : 'Informe um comprimento maior que zero.',
    };

    const widthCol: ColumnDef<Part> = {
      key: 'width',
      header: dimensionHeader('LARGURA', unit),
      width: 'minmax(48px, 0.55fr)',
      align: 'end',
      editor: 'dimension',
      inputMode: dimInputMode,
      title: `Largura da peça, em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) — ex.: ${UNIT_EXAMPLE[unit]}. Aceita vírgula e frações (16 1/2").`,
      errorHint: dimHint,
      format: (p) => showDim(p.width),
      toInput: (p) => showDim(p.width),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '') return { width: 0 };
        const v = parseDimension(text, unit);
        if (v === null || v < 0) return null;
        return { width: v };
      },
      validate: (p) =>
        !p.enabled || isBlankPart(p) || p.width > 0 ? null : 'Informe uma largura maior que zero.',
    };

    const cols: Array<ColumnDef<Part>> =
      settings.dimensionOrder === 'widthFirst' ? [widthCol, lengthCol] : [lengthCol, widthCol];

    cols.push({
      key: 'qty',
      header: 'QTD',
      width: 'minmax(32px, 0.3fr)',
      align: 'end',
      editor: 'integer',
      title: 'Quantidade de cópias desta peça. Zero mantém a linha sem usá-la.',
      errorHint: 'Quantidade inválida. Digite um número inteiro, sem vírgula (ex.: 12).',
      format: (p) => String(p.qty),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '') return { qty: 0 };
        const n = parseIntegerText(text);
        return n === null ? null : { qty: n };
      },
      validate: (p) =>
        !p.enabled || isBlankPart(p) || p.qty > 0
          ? null
          : 'Quantidade zero: esta peça não entra no cálculo.',
    });

    if (settings.labelsOnPanels) {
      cols.push({
        key: 'label',
        header: 'NOME',
        width: 'minmax(36px, 2fr)',
        editor: 'text',
        title: 'Nome desenhado dentro da peça no diagrama.',
        format: (p) => p.label,
        parse: (raw) => ({ label: raw.trimStart() }),
      });
    }

    if (settings.considerMaterial) {
      cols.push({
        key: 'material',
        header: 'MATERIAL',
        width: 'minmax(48px, 2fr)',
        editor: 'text',
        datalist: materials,
        title:
          'Material da peça. Escolha um já cadastrado ou digite um novo para criá-lo. A peça só é cortada de chapas do mesmo material.',
        format: (p) => p.material,
        parse: (raw) => ({ material: raw.trim() }),
        validate: (p) =>
          !p.enabled || isBlankPart(p) || p.material.trim() !== ''
            ? null
            : 'Escolha o material da peça.',
      });
    }

    if (settings.considerGrain) {
      cols.push({
        key: 'orientation',
        header: 'VEIO',
        width: '28px',
        align: 'center',
        editor: 'grain',
        title: 'Direção do veio: indiferente, ao longo da largura ou do comprimento.',
        format: (p) => String(p.orientation),
        parse: (raw) => {
          const n = Number(raw);
          if (n === 0 || n === 1 || n === 2) return { orientation: n };
          return null;
        },
      });
    }

    if (settings.edgeBandingEnabled) {
      cols.push({
        key: 'edgeBanding',
        header: 'FITA',
        width: '30px',
        align: 'center',
        editor: 'edgebanding',
        title:
          edgeBands.length === 0
            ? 'Fita de borda: cadastre um tipo de fita para poder aplicá-la.'
            : 'Fita de borda por lado da peça.',
        format: (p) =>
          [p.edgeBanding.top, p.edgeBanding.right, p.edgeBanding.bottom, p.edgeBanding.left]
            .map((v) => (v ? '1' : '0'))
            .join(''),
        parse: () => null,
        onActivate: (p) => onOpenEdgeBanding(p.id),
      });
    }

    cols.push({
      key: 'color',
      header: 'COR',
      width: '24px',
      align: 'center',
      editor: 'color',
      title: 'Cor da peça no diagrama.',
      errorHint: 'Cor inválida. Use o formato #RRGGBB (ex.: #A8D3E8).',
      format: (p) => p.color,
      parse: (raw) => (isValidHexColor(raw) ? { color: raw } : null),
    });

    cols.push({
      key: 'enabled',
      header: 'ATIVA',
      width: '30px',
      align: 'center',
      editor: 'checkbox',
      title: 'Linha desativada continua salva, mas fica fora do cálculo.',
      format: (p) => (p.enabled ? '1' : '0'),
      parse: (raw) => ({ enabled: raw === '1' }),
    });

    return cols;
  }, [unit, settings.dimensionOrder, settings.labelsOnPanels, settings.considerMaterial, settings.considerGrain, settings.edgeBandingEnabled, materials, edgeBands, onOpenEdgeBanding]);

  // ---------------------------------------------------------------------------
  // ERROS POR LINHA
  // ---------------------------------------------------------------------------
  const rowIssues = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const p of parts) {
      if (!p.enabled || isBlankPart(p)) continue;
      const msgs = validatePart(p, settings);
      if (msgs.length > 0) out[p.id] = msgs;
    }
    return out;
  }, [parts, settings]);

  // ---------------------------------------------------------------------------
  // RESUMO E ACOES
  // ---------------------------------------------------------------------------
  // A linha em branco automatica do fim da grade NAO entra na conta: ela ainda
  // nao e uma peca, so um lugar para digitar a proxima.
  const totalQty = useMemo(
    () =>
      parts.reduce(
        (acc, p) => (p.enabled && !isBlankPart(p) ? acc + Math.max(0, p.qty) : acc),
        0,
      ),
    [parts],
  );
  const filledRows = useMemo(() => parts.filter((p) => !isBlankPart(p)).length, [parts]);
  const errorCount = Object.keys(rowIssues).length;

  const handleDelete = useCallback(
    (ids: Id[]) => {
      onDelete(ids);
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    },
    [onDelete],
  );

  const applyPaste = useCallback(() => {
    const text = pasteText;
    setPasteText('');
    setPasteOpen(false);
    if (text.trim() === '') return;
    // Descarta as linhas em branco do fim antes de anexar as novas, senao a
    // linha vazia automatica ficaria encalhada no meio da tabela.
    const trailing: Id[] = [];
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      if (!isBlankPart(p)) break;
      trailing.push(p.id);
    }
    if (trailing.length > 0) onDelete(trailing);
    onPaste(text);
  }, [pasteText, parts, onDelete, onPaste]);

  const handleClear = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setSelected([]);
    onClear();
  }, [confirmClear, onClear]);

  return (
    <section className="grid-panel" aria-labelledby="grid-parts-title">
      <header className="grid-panel__head">
        <h2 className="grid-panel__title" id="grid-parts-title">
          Peças
        </h2>
        <span className="grid-panel__count">
          {filledRows} {filledRows === 1 ? 'linha' : 'linhas'} · {totalQty}{' '}
          {totalQty === 1 ? 'peça' : 'peças'}
        </span>
        <UnitChip unit={unit} onOpenSettings={onOpenSettings} />
        {errorCount > 0 ? (
          <span className="grid-panel__badge" role="status">
            {errorCount} {errorCount === 1 ? 'linha com erro' : 'linhas com erro'}
          </span>
        ) : null}
      </header>

      <EditableGrid<Part>
        columns={columns}
        rows={parts}
        getRowId={(p) => p.id}
        onChangeRow={(id, patch) => onChange(id, patch)}
        onAddRow={onAdd}
        onDeleteRows={handleDelete}
        onDuplicateRows={onDuplicate}
        onPaste={onPaste}
        onReorder={onReorder}
        selectedIds={selected}
        onSelectionChange={setSelected}
        rowIssues={rowIssues}
        emptyMessage="Nenhuma peça ainda. Digite as medidas ou cole a lista do Excel."
        ariaLabel="Tabela de peças"
        highlightedRowId={highlightedPartId ?? null}
        onHoverRow={onHoverPart}
        isRowEmpty={isBlankPart}
      />

      <div className="grid-toolbar" role="toolbar" aria-label="Ações da tabela de peças">
        <button type="button" className="btn btn--sm" onClick={onAdd} title="Adicionar linha (Ctrl+Enter)">
          + Linha
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => onDuplicate(selected)}
          disabled={selected.length === 0}
          title="Duplicar as linhas selecionadas (Ctrl+Shift+D)"
        >
          ⧉ Duplicar
        </button>
        <button
          type="button"
          className="btn btn--sm btn--danger"
          onClick={() => handleDelete(selected)}
          disabled={selected.length === 0}
          title="Remover as linhas selecionadas (Ctrl+Del)"
        >
          ✕ Remover
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => setPasteOpen((v) => !v)}
          aria-expanded={pasteOpen}
          title="Colar uma lista copiada do Excel ou do Google Planilhas"
        >
          Colar do Excel
        </button>
        <span className="grid-toolbar__spacer" />
        <button
          type="button"
          className={`btn btn--sm${confirmClear ? ' btn--danger' : ' btn--ghost'}`}
          onClick={handleClear}
          onBlur={() => setConfirmClear(false)}
          title="Apagar todas as peças"
        >
          {confirmClear ? 'Confirmar limpeza?' : 'Limpar'}
        </button>
      </div>

      {pasteOpen ? (
        <div className="grid-paste">
          <label className="grid-paste__label" htmlFor="grid-paste-parts">
            Cole abaixo (uma peça por linha: comprimento, largura, quantidade, nome, material). As
            medidas serão lidas em {UNIT_NAME[unit]} ({UNIT_ABBR[unit]}):
          </label>
          <textarea
            id="grid-paste-parts"
            className="grid-paste__area"
            rows={5}
            value={pasteText}
            spellCheck={false}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={`${PASTE_EXAMPLE[unit]}\t4\tLateral\tMDF 15 mm`}
          />
          <div className="grid-paste__actions">
            <button type="button" className="btn btn--sm btn--primary" onClick={applyPaste}>
              Adicionar linhas
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                setPasteText('');
                setPasteOpen(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
